import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import Alerta from "./Alertas";

import {
  auth,
  db,
} from "./../firebase/firebaseConfig";

import AgregarAlertas from "../firebase/AgregarUsuariosAlertas";
import ExportarExcel from "../funciones/ExportarExcel";
import { UserContext } from "../context/UserContext";


// ======================================================
// COLORES / ESTILOS
// ======================================================

const COLORS = {
  background: "#f8fafc",
  card: "#ffffff",
  border: "#e5e9ef",

  title: "#172b4d",
  text: "#344563",
  muted: "#6b778c",

  teal: "#0d9da5",
  tealSoft: "#e9f7f8",

  blue: "#2185d0",
  blueSoft: "#eef6fc",

  green: "#21ba45",
  greenSoft: "#eefaf1",

  orange: "#f2711c",
  orangeSoft: "#fff4ec",

  graySoft: "#f4f5f7",
};


const cardStyle = {
  margin: 0,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(9, 30, 66, 0.08)",
  background: COLORS.card,
};


const stickyHeader = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f8fafc",
  color: "#42526e",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${COLORS.border}`,
};


// ======================================================
// ROLES / DOMINIOS
// ======================================================

const ROLES_HC = [
  "ADMIN-HC",
  "SUPERVISOR-HC",
  "TRANSPORTE-HC",
];


const ROLES_ST = [
  "ADMIN-LMST",
  "TECNICO-LMST",
];


const obtenerDominioRol = (rol) => {

  if (ROLES_HC.includes(rol)) {
    return "HC";
  }

  if (ROLES_ST.includes(rol)) {
    return "ST";
  }

  return "";
};


// ======================================================
// HELPERS
// ======================================================

const normalizarTexto = (value) =>
  String(value ?? "").trim();


const normalizarCorreo = (value) =>
  normalizarTexto(value).toLowerCase();


const nombreCompleto = (usuario) =>
  [
    usuario?.nombre,
    usuario?.apellido,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();


const nombreRol = (rol) => {

  const nombres = {

    "ADMIN-HC":
      "Administrador HomeCare",

    "SUPERVISOR-HC":
      "Supervisor HomeCare",

    "TRANSPORTE-HC":
      "Transporte HomeCare",

    "ADMIN-LMST":
      "Administrador Servicio Técnico",

    "TECNICO-LMST":
      "Técnico Servicio Técnico",

  };

  return nombres[rol] || rol || "-";
};


// ======================================================
// COMPONENTE
// ======================================================

export const UsuariosEnvios = () => {

  const { users } =
    useContext(UserContext);


  // ====================================================
  // USUARIO AUTENTICADO
  // ====================================================

  const uidActual =
    auth?.currentUser?.uid || "";

  const correoActual =
    normalizarCorreo(
      auth?.currentUser?.email
    );


  // ====================================================
  // FORMULARIO
  // ====================================================

  const [
    correo,
    setCorreo,
  ] = useState("");

  const [
    nombre,
    setNombre,
  ] = useState("");

  const [
    apellido,
    setApellido,
  ] = useState("");

  const [
    entidad,
    setEntidad,
  ] = useState("");


  // ====================================================
  // ALERTAS REALES
  // ====================================================

  const [
    salida,
    setSalida,
  ] = useState(false);

  const [
    mantencion,
    setMantencion,
  ] = useState(false);


  // ====================================================
  // DATOS
  // ====================================================

  const [
    empresaActual,
    setEmpresaActual,
  ] = useState(null);

  const [
    usuarios,
    setUsuarios,
  ] = useState([]);

  const [
    usuarioAlert,
    setUsuarioAlert,
  ] = useState([]);


  // ====================================================
  // UI
  // ====================================================

  const [
    alerta,
    cambiarAlerta,
  ] = useState({});

  const [
    estadoAlerta,
    cambiarEstadoAlerta,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    isLoading,
    setIsLoading,
  ] = useState(true);

  const [
    errorCarga,
    setErrorCarga,
  ] = useState("");

  const [
    buscador,
    setBuscador,
  ] = useState("");

  const [
    buscadorDebounced,
    setBuscadorDebounced,
  ] = useState("");


  // ====================================================
  // DOMINIO
  // ====================================================

  const dominioActual =
    useMemo(
      () =>
        obtenerDominioRol(
          users?.rol
        ),
      [users?.rol]
    );


  // ====================================================
  // DEBOUNCE BUSCADOR
  // ====================================================

  useEffect(() => {

    const timer =
      setTimeout(() => {

        setBuscadorDebounced(
          buscador
        );

      }, 250);


    return () => {
      clearTimeout(timer);
    };

  }, [buscador]);


  // ====================================================
  // ALERTA
  // ====================================================

  const mostrarAlerta =
    useCallback(
      (
        tipo,
        mensaje
      ) => {

        cambiarAlerta({
          tipo,
          mensaje,
        });

        cambiarEstadoAlerta(true);

      },
      []
    );


  // ====================================================
  // LIMPIAR FORMULARIO
  // ====================================================

  const limpiarUsuario =
    useCallback(() => {

      setCorreo("");
      setNombre("");
      setApellido("");
      setEntidad("");

    }, []);


  const limpiarChecks =
    useCallback(() => {

      setSalida(false);
      setMantencion(false);

    }, []);


  const limpiarFormulario =
    useCallback(() => {

      limpiarUsuario();
      limpiarChecks();

    }, [
      limpiarUsuario,
      limpiarChecks,
    ]);


  // ====================================================
  // CARGAR DATOS
  // ====================================================

  const cargarDatos =
    useCallback(
      async () => {

        if (!uidActual) {
          return;
        }

        setIsLoading(true);
        setErrorCarga("");

        cambiarEstadoAlerta(false);
        cambiarAlerta({});


        try {

          // ============================================
          // 1. USUARIO AUTENTICADO
          // ============================================

          const usuarioRef =
            doc(
              db,
              "usuarios",
              uidActual
            );


          const snapUsuarioActual =
            await getDoc(
              usuarioRef
            );


          if (!snapUsuarioActual.exists()) {

            throw new Error(
              "No se encontró el usuario autenticado en la colección usuarios."
            );

          }


          const usuarioActual = {

            id:
              snapUsuarioActual.id,

            ...snapUsuarioActual.data(),

          };


          if (
            usuarioActual.activo === false
          ) {

            throw new Error(
              "El usuario autenticado se encuentra desactivado."
            );

          }


          if (!usuarioActual.emp_id) {

            throw new Error(
              "El usuario autenticado no tiene empresa asignada."
            );

          }


          setEmpresaActual({

            emp_id:
              usuarioActual.emp_id,

            empresa:
              usuarioActual.empresa || "",

          });


          // ============================================
          // 2. USUARIOS DE LA EMPRESA
          // ============================================

          const qUsuariosEmpresa =
            query(

              collection(
                db,
                "usuarios"
              ),

              where(
                "emp_id",
                "==",
                usuarioActual.emp_id
              )

            );


          const snapUsuarios =
            await getDocs(
              qUsuariosEmpresa
            );


          let arrUsuarios =
            snapUsuarios.docs

              .map(
                (u) => ({
                  id: u.id,
                  ...u.data(),
                })
              )

              .filter(
                (u) =>
                  u.activo !== false
              );


          // ============================================
          // SEPARACIÓN DE DOMINIO
          //
          // HC solo ve usuarios HC.
          // ST solo ve usuarios ST.
          // ============================================

          const dominioUsuario =
            obtenerDominioRol(
              usuarioActual.rol
            );


          if (
            dominioUsuario === "HC"
          ) {

            arrUsuarios =
              arrUsuarios.filter(
                (u) =>
                  ROLES_HC.includes(
                    u.rol
                  )
              );

          }


          if (
            dominioUsuario === "ST"
          ) {

            arrUsuarios =
              arrUsuarios.filter(
                (u) =>
                  ROLES_ST.includes(
                    u.rol
                  )
              );

          }


          arrUsuarios.sort(
            (a, b) =>
              nombreCompleto(a)
                .localeCompare(
                  nombreCompleto(b),
                  "es",
                  {
                    sensitivity:
                      "base",
                  }
                )
          );


          setUsuarios(
            arrUsuarios
          );


          // ============================================
          // 3. ALERTAS CONFIGURADAS DE LA EMPRESA
          // ============================================

          const qAlertasEmpresa =
            query(

              collection(
                db,
                "usuariosalertas"
              ),

              where(
                "emp_id",
                "==",
                usuarioActual.emp_id
              )

            );


          const snapAlertas =
            await getDocs(
              qAlertasEmpresa
            );


          const arrAlertas =
            snapAlertas.docs

              .map(
                (a) => ({
                  id: a.id,
                  ...a.data(),
                })
              )

              .sort(
                (a, b) =>
                  normalizarTexto(
                    a.nombre
                  )
                    .localeCompare(
                      normalizarTexto(
                        b.nombre
                      ),
                      "es",
                      {
                        sensitivity:
                          "base",
                      }
                    )
              );


          setUsuarioAlert(
            arrAlertas
          );

        } catch (error) {

          console.error(
            "Error cargando configuración de alertas:",
            error
          );


          setEmpresaActual(null);
          setUsuarios([]);
          setUsuarioAlert([]);


          setErrorCarga(
            error?.message ||
            "No se pudieron cargar los usuarios y alertas de la empresa."
          );

        } finally {

          setIsLoading(false);

        }

      },
      [
        uidActual,
      ]
    );


  // ====================================================
  // CARGA INICIAL
  // ====================================================

  useEffect(() => {

    cargarDatos();

  }, [
    cargarDatos,
  ]);


  // ====================================================
  // USUARIOS DISPONIBLES
  // ====================================================

  const usuariosDisponibles =
    useMemo(
      () => {

        return usuarios.filter(
          (usuario) => {

            const email =
              normalizarCorreo(
                usuario.correo
              );


            if (!email) {
              return false;
            }


            const existeEnAlertas =
              usuarioAlert.some(
                (alertaUsuario) =>
                  normalizarCorreo(
                    alertaUsuario.correo
                  ) === email
              );


            return !existeEnAlertas;

          }
        );

      },
      [
        usuarios,
        usuarioAlert,
      ]
    );


  // ====================================================
  // OPCIONES DROPDOWN
  // ====================================================

  const opcionesUsuarios =
    useMemo(
      () => {

        return usuariosDisponibles.map(
          (usuario) => {

            const nombreUsuario =
              nombreCompleto(
                usuario
              );


            const email =
              normalizarCorreo(
                usuario.correo
              );


            return {

              key:
                usuario.id,

              value:
                email,

              text:
                nombreUsuario
                  ? `${nombreUsuario} · ${email}`
                  : email,

              description:
                nombreRol(
                  usuario.rol
                ),

            };

          }
        );

      },
      [
        usuariosDisponibles,
      ]
    );


  // ====================================================
  // SELECCIONAR USUARIO
  // ====================================================

  const seleccionarUsuario =
    useCallback(
      (
        emailSeleccionado
      ) => {

        cambiarEstadoAlerta(false);
        cambiarAlerta({});


        const email =
          normalizarCorreo(
            emailSeleccionado
          );


        limpiarChecks();


        if (!email) {

          limpiarUsuario();
          return;

        }


        const usuario =
          usuarios.find(
            (u) =>
              normalizarCorreo(
                u.correo
              ) === email
          );


        if (!usuario) {

          mostrarAlerta(
            "error",
            "El usuario seleccionado no pertenece a los usuarios disponibles."
          );

          limpiarUsuario();

          return;

        }


        const existeEnAlerta =
          usuarioAlert.some(
            (u) =>
              normalizarCorreo(
                u.correo
              ) === email
          );


        if (existeEnAlerta) {

          mostrarAlerta(
            "error",
            "Este usuario ya tiene una configuración de alertas."
          );

          limpiarUsuario();

          return;

        }


        setCorreo(
          normalizarCorreo(
            usuario.correo
          )
        );


        setNombre(
          normalizarTexto(
            usuario.nombre
          )
        );


        setApellido(
          normalizarTexto(
            usuario.apellido
          )
        );


        setEntidad(
          normalizarTexto(
            usuario.empresa ||
            empresaActual?.empresa
          )
        );

      },
      [
        usuarios,
        usuarioAlert,
        empresaActual?.empresa,
        limpiarChecks,
        limpiarUsuario,
        mostrarAlerta,
      ]
    );


  // ====================================================
  // VALIDAR AL MENOS UNA ALERTA
  // ====================================================

  const tieneAlertasSeleccionadas =
    useMemo(
      () =>
        salida ||
        mantencion,
      [
        salida,
        mantencion,
      ]
    );


  // ====================================================
  // GUARDAR
  // ====================================================

  const handleSubmit =
    async () => {

      cambiarEstadoAlerta(false);
      cambiarAlerta({});


      const email =
        normalizarCorreo(
          correo
        );


      if (
        !empresaActual?.emp_id
      ) {

        mostrarAlerta(
          "error",
          "No se pudo identificar la empresa actual."
        );

        return;

      }


      if (!email) {

        mostrarAlerta(
          "error",
          "Debe seleccionar un usuario."
        );

        return;

      }


      const usuario =
        usuarios.find(
          (u) =>
            normalizarCorreo(
              u.correo
            ) === email
        );


      if (!usuario) {

        mostrarAlerta(
          "error",
          "El usuario seleccionado no pertenece a esta empresa."
        );

        return;

      }


      if (
        usuario.emp_id !==
        empresaActual.emp_id
      ) {

        mostrarAlerta(
          "error",
          "No puede asignar alertas a usuarios de otra empresa."
        );

        return;

      }


      if (
        !tieneAlertasSeleccionadas
      ) {

        mostrarAlerta(
          "error",
          "Debe seleccionar al menos una alerta."
        );

        return;

      }


      setIsSaving(true);


      try {

        // ============================================
        // VALIDACIÓN FRESCA
        // ============================================

        const qExiste =
          query(

            collection(
              db,
              "usuariosalertas"
            ),

            where(
              "emp_id",
              "==",
              empresaActual.emp_id
            ),

            where(
              "correo",
              "==",
              email
            ),

            limit(1)

          );


        const snapExiste =
          await getDocs(
            qExiste
          );


        if (!snapExiste.empty) {

          mostrarAlerta(
            "error",
            "Este usuario ya tiene una configuración de alertas."
          );

          limpiarFormulario();

          await cargarDatos();

          return;

        }


        // ============================================
        // GUARDAR
        //
        // Solo se almacenan las alertas que actualmente
        // existen en LogisticMed:
        //
        // salida
        // mantencion
        // ============================================

        const fechaActual =
          new Date();


        await AgregarAlertas({

          userAdd:
            correoActual,

          userMod:
            correoActual,

          fechaAdd:
            fechaActual,

          fechaMod:
            fechaActual,

          emp_id:
            empresaActual.emp_id,

          empresa:
            normalizarTexto(
              usuario.empresa ||
              empresaActual.empresa
            ),

          nombre:
            nombreCompleto(
              usuario
            ),

          correo:
            email,

          salida:
            Boolean(salida),

          mantencion:
            Boolean(mantencion),

        });


        limpiarFormulario();


        await cargarDatos();


        mostrarAlerta(
          "exito",
          "Configuración de alertas guardada correctamente."
        );

      } catch (error) {

        console.error(
          "Error guardando configuración de alertas:",
          error
        );


        await Swal.fire({

          icon:
            "error",

          title:
            "Error",

          text:
            "No fue posible guardar la configuración de alertas. Intente nuevamente.",

          confirmButtonColor:
            COLORS.teal,

        });

      } finally {

        setIsSaving(false);

      }

    };


  // ====================================================
  // FILTRO
  // ====================================================

  const usuarioAlertFiltrado =
    useMemo(
      () => {

        const texto =
          normalizarTexto(
            buscadorDebounced
          )
            .toLowerCase();


        if (!texto) {
          return usuarioAlert;
        }


        return usuarioAlert.filter(
          (usuario) => {

            const campos = [
              usuario.correo,
              usuario.nombre,
              usuario.empresa,
            ];


            return campos.some(
              (campo) =>
                normalizarTexto(
                  campo
                )
                  .toLowerCase()
                  .includes(
                    texto
                  )
            );

          }
        );

      },
      [
        usuarioAlert,
        buscadorDebounced,
      ]
    );


  // ====================================================
  // EXPORTAR EXCEL
  // ====================================================

  const ExportarXls =
    () => {

      if (
        usuarioAlertFiltrado.length === 0
      ) {
        return;
      }


      const datosExcel =
        usuarioAlertFiltrado.map(
          (item) => ({

            Empresa:
              item.empresa || "",

            Nombre:
              item.nombre || "",

            Correo:
              item.correo || "",

            Salidas_y_Traspasos:
              item.salida
                ? "SI"
                : "NO",

            Mantenciones:
              item.mantencion
                ? "SI"
                : "NO",

          })
        );


      const columnsToShow = [
        "Empresa",
        "Nombre",
        "Correo",
        "Salidas_y_Traspasos",
        "Mantenciones",
      ];


      try {

        const excelBlob =
          ExportarExcel(
            datosExcel,
            columnsToShow
          );


        const excelURL =
          URL.createObjectURL(
            excelBlob
          );


        const downloadLink =
          document.createElement(
            "a"
          );


        downloadLink.href =
          excelURL;

        downloadLink.download =
          "alertas_usuarios.xlsx";


        document.body.appendChild(
          downloadLink
        );


        downloadLink.click();

        downloadLink.remove();


        setTimeout(
          () => {

            URL.revokeObjectURL(
              excelURL
            );

          },
          1000
        );

      } catch (error) {

        console.error(
          "Error exportando alertas:",
          error
        );


        Swal.fire({

          icon:
            "error",

          title:
            "Error",

          text:
            "No fue posible generar el archivo Excel.",

          confirmButtonColor:
            COLORS.teal,

        });

      }

    };


  // ====================================================
  // KPI
  // ====================================================

  const kpis =
    useMemo(
      () => {

        return {

          disponibles:
            usuariosDisponibles.length,

          configurados:
            usuarioAlert.length,

        };

      },
      [
        usuariosDisponibles.length,
        usuarioAlert.length,
      ]
    );


  // ====================================================
  // ICONO ESTADO ALERTA
  // ====================================================

  const renderEstado =
    (
      activo,
      color
    ) => {

      return (

        <Icon
          name={
            activo
              ? "check circle"
              : "minus circle"
          }

          style={{
            margin: 0,

            color:
              activo
                ? color
                : "#c1c7d0",
          }}

          title={
            activo
              ? "Activo"
              : "No configurado"
          }
        />

      );

    };


  // ====================================================
  // RETURN
  // ====================================================

  return (

    <div
      style={{
        minHeight: "100vh",

        background:
          COLORS.background,

        padding:
          "24px 18px 40px",
      }}
    >

      <div
        style={{
          width: "100%",
          maxWidth: 1250,
          margin: "0 auto",
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div
          style={{
            display: "flex",
            alignItems: "center",

            justifyContent:
              "space-between",

            gap: 16,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >

            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 9,

                background:
                  COLORS.tealSoft,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                flexShrink: 0,
              }}
            >

              <Icon
                name="paper plane outline"
                size="large"

                style={{
                  color:
                    COLORS.teal,

                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  color:
                    COLORS.title,

                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                Alertas de Usuarios
              </div>


              <div
                style={{
                  color:
                    COLORS.muted,

                  fontSize: 13,
                  marginTop: 4,
                }}
              >
                Configuración de usuarios que reciben notificaciones operativas
              </div>

            </div>

          </div>


          <Button
            color="green"
            size="small"
            icon
            labelPosition="left"

            disabled={
              isLoading ||
              usuarioAlertFiltrado.length === 0
            }

            onClick={
              ExportarXls
            }
          >
            <Icon name="file excel" />
            Exportar Excel
          </Button>

        </div>


        {/* =================================================
            ERROR
        ================================================= */}

        {errorCarga && (

          <Message
            negative
            icon

            style={{
              marginBottom: 18,
            }}
          >

            <Icon name="warning circle" />

            <Message.Content>

              <Message.Header>
                No fue posible cargar la configuración
              </Message.Header>

              {errorCarga}

            </Message.Content>

          </Message>

        )}


        {/* =================================================
            KPIs
        ================================================= */}

        <div
          style={{
            display: "grid",

            gridTemplateColumns:
              "repeat(auto-fit, minmax(220px, 1fr))",

            gap: 14,
            marginBottom: 18,
          }}
        >

          {/* EMPRESA */}

          <div
            style={{
              ...cardStyle,

              padding:
                "15px 17px",

              display: "flex",
              alignItems: "center",
              gap: 13,
            }}
          >

            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,

                background:
                  COLORS.tealSoft,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <Icon
                name="building outline"

                style={{
                  color:
                    COLORS.teal,

                  margin: 0,
                }}
              />

            </div>


            <div
              style={{
                minWidth: 0,
              }}
            >

              <div
                style={{
                  color:
                    COLORS.muted,

                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Empresa
              </div>

              <div
                style={{
                  color:
                    COLORS.title,

                  fontSize: 14,
                  fontWeight: 700,

                  overflow: "hidden",

                  textOverflow:
                    "ellipsis",

                  whiteSpace:
                    "nowrap",
                }}
              >
                {empresaActual?.empresa || "-"}
              </div>

            </div>

          </div>


          {/* DISPONIBLES */}

          <div
            style={{
              ...cardStyle,

              padding:
                "15px 17px",

              display: "flex",
              alignItems: "center",
              gap: 13,
            }}
          >

            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,

                background:
                  COLORS.blueSoft,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <Icon
                name="user plus"

                style={{
                  color:
                    COLORS.blue,

                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  color:
                    COLORS.muted,

                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Usuarios disponibles
              </div>

              <div
                style={{
                  color:
                    COLORS.title,

                  fontSize: 21,
                  lineHeight: 1.2,
                  fontWeight: 700,
                }}
              >
                {kpis.disponibles}
              </div>

            </div>

          </div>


          {/* CONFIGURADOS */}

          <div
            style={{
              ...cardStyle,

              padding:
                "15px 17px",

              display: "flex",
              alignItems: "center",
              gap: 13,
            }}
          >

            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,

                background:
                  COLORS.greenSoft,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >

              <Icon
                name="bell outline"

                style={{
                  color:
                    COLORS.green,

                  margin: 0,
                }}
              />

            </div>


            <div>

              <div
                style={{
                  color:
                    COLORS.muted,

                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                Con alertas
              </div>

              <div
                style={{
                  color:
                    COLORS.title,

                  fontSize: 21,
                  lineHeight: 1.2,
                  fontWeight: 700,
                }}
              >
                {kpis.configurados}
              </div>

            </div>

          </div>

        </div>


        {/* =================================================
            FORMULARIO
        ================================================= */}

        <Segment
          style={{
            ...cardStyle,

            padding: 0,
            marginBottom: 18,

            overflow: "visible",
          }}
        >

          {/* HEADER FORMULARIO */}

          <div
            style={{
              padding:
                "14px 18px",

              borderBottom:
                `1px solid ${COLORS.border}`,

              display: "flex",
              alignItems: "center",
              gap: 9,
            }}
          >

            <Icon
              name="bell outline"

              style={{
                color:
                  COLORS.teal,

                margin: 0,
              }}
            />

            <div>

              <div
                style={{
                  color:
                    COLORS.title,

                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                Nueva configuración
              </div>

              <div
                style={{
                  color:
                    COLORS.muted,

                  fontSize: 11,
                  marginTop: 2,
                }}
              >
                Seleccione un usuario y las alertas que debe recibir
              </div>

            </div>

          </div>


          <div
            style={{
              padding:
                "18px 18px 16px",
            }}
          >

            {isLoading ? (

              <div
                style={{
                  position:
                    "relative",

                  height: 180,
                }}
              >

                <Dimmer
                  active
                  inverted
                >
                  <Loader>
                    Cargando usuarios...
                  </Loader>
                </Dimmer>

              </div>

            ) : (

              <Form>

                {/* USUARIO */}

                <Form.Group
                  widths="equal"
                >

                  <Form.Field>

                    <label>
                      Usuario
                    </label>

                    <Dropdown
                      fluid
                      search
                      selection
                      clearable

                      placeholder="Seleccione un usuario"

                      value={
                        correo || ""
                      }

                      options={
                        opcionesUsuarios
                      }

                      onChange={(
                        e,
                        { value }
                      ) =>
                        seleccionarUsuario(
                          value
                        )
                      }

                      disabled={
                        isSaving ||
                        isLoading
                      }

                      noResultsMessage="No hay usuarios disponibles"
                    />

                  </Form.Field>


                  <Form.Field>

                    <label>
                      Nombre
                    </label>

                    <Input
                      fluid
                      readOnly

                      placeholder="Nombre del usuario"

                      value={
                        `${nombre} ${apellido}`
                          .trim()
                      }

                      icon={{
                        name:
                          "user outline",

                        color:
                          "blue",
                      }}

                      iconPosition="left"
                    />

                  </Form.Field>


                  <Form.Field>

                    <label>
                      Empresa
                    </label>

                    <Input
                      fluid
                      readOnly

                      placeholder="Empresa"

                      value={
                        entidad
                      }

                      icon={{
                        name:
                          "building outline",

                        color:
                          "blue",
                      }}

                      iconPosition="left"
                    />

                  </Form.Field>

                </Form.Group>


                {usuariosDisponibles.length === 0 && (

                  <Message
                    info
                    size="small"
                  >
                    <Icon name="info circle" />

                    Todos los usuarios activos disponibles ya tienen configuración de alertas.
                  </Message>

                )}


                {/* =================================================
                    ALERTAS
                ================================================= */}

                <div
                  style={{
                    marginTop: 18,
                    marginBottom: 12,
                  }}
                >

                  <Header
                    as="h5"

                    style={{
                      color:
                        COLORS.title,

                      marginBottom: 4,
                    }}
                  >
                    Alertas que recibirá el usuario
                  </Header>


                  <div
                    style={{
                      color:
                        COLORS.muted,

                      fontSize: 12,
                    }}
                  >
                    Active una o ambas categorías de notificación.
                  </div>

                </div>


                <div
                  style={{
                    display: "grid",

                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(260px, 1fr))",

                    gap: 10,
                  }}
                >

                  {/* SALIDAS Y TRASPASOS */}

                  <AlertOption
                    icon="exchange"

                    label="Salidas y traspasos"

                    description="Notificaciones de movimientos y traslados de equipos"

                    color={
                      COLORS.orange
                    }

                    background={
                      COLORS.orangeSoft
                    }

                    checked={
                      salida
                    }

                    disabled={
                      isSaving ||
                      !correo
                    }

                    onChange={
                      setSalida
                    }
                  />


                  {/* MANTENCIONES */}

                  <AlertOption
                    icon="calendar check outline"

                    label="Mantenciones"

                    description="Mantenciones próximas a vencer y vencidas"

                    color={
                      COLORS.teal
                    }

                    background={
                      COLORS.tealSoft
                    }

                    checked={
                      mantencion
                    }

                    disabled={
                      isSaving ||
                      !correo
                    }

                    onChange={
                      setMantencion
                    }
                  />

                </div>


                {/* ACCIONES */}

                <div
                  style={{
                    display: "flex",

                    justifyContent:
                      "flex-end",

                    gap: 8,
                    marginTop: 20,
                  }}
                >

                  <Button
                    basic
                    type="button"

                    disabled={
                      isSaving ||
                      (
                        !correo &&
                        !tieneAlertasSeleccionadas
                      )
                    }

                    onClick={
                      limpiarFormulario
                    }
                  >
                    <Icon name="eraser" />
                    Limpiar
                  </Button>


                  <Button
                    type="button"
                    color="teal"

                    loading={
                      isSaving
                    }

                    disabled={
                      isSaving ||
                      isLoading ||
                      !correo ||
                      !tieneAlertasSeleccionadas
                    }

                    onClick={
                      handleSubmit
                    }
                  >
                    <Icon name="save" />
                    Guardar configuración
                  </Button>

                </div>

              </Form>

            )}

          </div>

        </Segment>


        {/* =================================================
            LISTADO
        ================================================= */}

        <Segment
          style={{
            ...cardStyle,

            padding: 0,
            overflow: "hidden",
          }}
        >

          {/* HEADER LISTADO */}

          <div
            style={{
              padding:
                "13px 16px",

              borderBottom:
                `1px solid ${COLORS.border}`,

              display: "flex",
              alignItems: "center",

              justifyContent:
                "space-between",

              gap: 12,
              flexWrap: "wrap",
            }}
          >

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
              }}
            >

              <Icon
                name="users"

                style={{
                  color:
                    COLORS.teal,

                  margin: 0,
                }}
              />


              <div>

                <div
                  style={{
                    color:
                      COLORS.title,

                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  Usuarios configurados
                </div>


                <div
                  style={{
                    color:
                      COLORS.muted,

                    fontSize: 11,
                    marginTop: 2,
                  }}
                >
                  {usuarioAlertFiltrado.length} registro
                  {usuarioAlertFiltrado.length === 1
                    ? ""
                    : "s"}
                </div>

              </div>

            </div>


            <Input
              size="small"

              placeholder="Buscar nombre, correo o empresa..."

              value={
                buscador
              }

              onChange={(e) =>
                setBuscador(
                  e.target.value
                )
              }

              icon={
                buscador
                  ? {
                      name:
                        "close",

                      link:
                        true,

                      onClick:
                        () =>
                          setBuscador(
                            ""
                          ),
                    }
                  : "search"
              }

              style={{
                width: 330,

                maxWidth:
                  "100%",
              }}
            />

          </div>


          {/* TABLA */}

          {isLoading ? (

            <div
              style={{
                position:
                  "relative",

                height: 240,
              }}
            >

              <Dimmer
                active
                inverted
              >
                <Loader>
                  Cargando configuraciones...
                </Loader>
              </Dimmer>

            </div>

          ) : usuarioAlertFiltrado.length === 0 ? (

            <div
              style={{
                minHeight: 220,

                display: "flex",
                alignItems: "center",

                justifyContent:
                  "center",

                padding: 30,
                textAlign: "center",
              }}
            >

              <div>

                <Icon
                  name="bell slash outline"
                  size="huge"

                  style={{
                    color:
                      "#c1c7d0",

                    marginBottom: 12,
                  }}
                />


                <Header
                  as="h4"

                  style={{
                    color:
                      COLORS.title,

                    marginBottom: 5,
                  }}
                >
                  Sin configuraciones
                </Header>


                <div
                  style={{
                    color:
                      COLORS.muted,

                    fontSize: 12,
                  }}
                >

                  {buscadorDebounced
                    ? "No existen usuarios que coincidan con la búsqueda."
                    : "Aún no existen usuarios configurados para recibir alertas."}

                </div>

              </div>

            </div>

          ) : (

            <div
              style={{
                maxHeight: 520,
                overflowX: "auto",
                overflowY: "auto",
              }}
            >

              <Table
                celled
                compact
                selectable
                unstackable

                style={{
                  margin: 0,
                  border: "none",

                  minWidth: 800,

                  fontSize: 12,
                }}
              >

                <Table.Header>

                  <Table.Row>

                    <Table.HeaderCell
                      style={{
                        ...stickyHeader,

                        width: 50,

                        textAlign:
                          "center",
                      }}
                    >
                      N°
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={
                        stickyHeader
                      }
                    >
                      Correo
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={
                        stickyHeader
                      }
                    >
                      Nombre
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={
                        stickyHeader
                      }
                    >
                      Empresa
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={{
                        ...stickyHeader,

                        textAlign:
                          "center",
                      }}
                    >
                      Salidas y traspasos
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={{
                        ...stickyHeader,

                        textAlign:
                          "center",
                      }}
                    >
                      Mantenciones
                    </Table.HeaderCell>


                    <Table.HeaderCell
                      style={{
                        ...stickyHeader,

                        width: 75,

                        textAlign:
                          "center",
                      }}
                    >
                      Acción
                    </Table.HeaderCell>

                  </Table.Row>

                </Table.Header>


                <Table.Body>

                  {usuarioAlertFiltrado.map(
                    (
                      item,
                      index
                    ) => (

                      <Table.Row
                        key={
                          item.id
                        }
                      >

                        <Table.Cell
                          textAlign="center"
                        >
                          {index + 1}
                        </Table.Cell>


                        <Table.Cell>
                          {item.correo || "-"}
                        </Table.Cell>


                        <Table.Cell
                          style={{
                            fontWeight: 600,

                            color:
                              COLORS.text,
                          }}
                        >
                          {item.nombre || "-"}
                        </Table.Cell>


                        <Table.Cell>
                          {item.empresa || "-"}
                        </Table.Cell>


                        <Table.Cell
                          textAlign="center"
                        >
                          {renderEstado(
                            item.salida,
                            COLORS.orange
                          )}
                        </Table.Cell>


                        <Table.Cell
                          textAlign="center"
                        >
                          {renderEstado(
                            item.mantencion,
                            COLORS.teal
                          )}
                        </Table.Cell>


                        <Table.Cell
                          textAlign="center"
                        >

                          <Button
                            as={Link}

                            to={`/configuracion/actualizaalerta/${item.id}`}

                            basic
                            icon
                            size="tiny"

                            title="Editar configuración"

                            style={{
                              color:
                                COLORS.blue,

                              background:
                                COLORS.blueSoft,

                              boxShadow:
                                "none",

                              border:
                                "1px solid #d8e9f7",
                            }}
                          >

                            <Icon
                              name="edit outline"

                              style={{
                                margin: 0,
                              }}
                            />

                          </Button>

                        </Table.Cell>

                      </Table.Row>

                    )
                  )}

                </Table.Body>

              </Table>

            </div>

          )}


          {/* FOOTER */}

          {!isLoading &&
            usuarioAlert.length > 0 && (

              <div
                style={{
                  padding:
                    "10px 16px",

                  borderTop:
                    `1px solid ${COLORS.border}`,

                  background:
                    "#fafbfc",

                  color:
                    COLORS.muted,

                  fontSize: 11,
                }}
              >

                Mostrando{" "}

                <strong>
                  {usuarioAlertFiltrado.length}
                </strong>

                {" "}de{" "}

                <strong>
                  {usuarioAlert.length}
                </strong>

                {" "}usuarios configurados

              </div>

            )}

        </Segment>


        {/* =================================================
            ALERTA
        ================================================= */}

        <Alerta
          tipo={
            alerta.tipo
          }

          mensaje={
            alerta.mensaje
          }

          estadoAlerta={
            estadoAlerta
          }

          cambiarEstadoAlerta={
            cambiarEstadoAlerta
          }
        />

      </div>

    </div>

  );

};


// ======================================================
// OPCIÓN DE ALERTA
// ======================================================

const AlertOption = ({
  icon,
  label,
  description,
  color,
  background,
  checked,
  disabled,
  onChange,
}) => {

  return (

    <div
      style={{
        minHeight: 76,

        padding:
          "12px 13px",

        border:
          `1px solid ${
            checked
              ? color
              : COLORS.border
          }`,

        background:
          checked
            ? background
            : "#ffffff",

        borderRadius: 7,

        display: "flex",
        alignItems: "center",
        gap: 11,

        opacity:
          disabled
            ? 0.65
            : 1,

        transition:
          "all 0.15s ease",
      }}
    >

      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 7,

          display: "flex",
          alignItems: "center",

          justifyContent:
            "center",

          background:
            background,

          flexShrink: 0,
        }}
      >

        <Icon
          name={icon}

          style={{
            color,
            margin: 0,
          }}
        />

      </div>


      <div
        style={{
          flex: 1,
          minWidth: 0,
        }}
      >

        <div
          style={{
            color:
              COLORS.title,

            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {label}
        </div>


        <div
          style={{
            color:
              COLORS.muted,

            fontSize: 10,
            marginTop: 2,
          }}
        >
          {description}
        </div>

      </div>


      <Checkbox
        toggle

        checked={
          Boolean(
            checked
          )
        }

        disabled={
          disabled
        }

        onChange={(
          e,
          data
        ) =>
          onChange(
            Boolean(
              data.checked
            )
          )
        }
      />

    </div>

  );

};


export default UsuariosEnvios;